import { useState } from 'react'
import { PlaneLanding, PlaneTakeoff } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { DetailLevelToggle } from './DetailLevelToggle'
import { VirtualHourList } from './VirtualHourList'
import type { DetailLevel, MovementCallbacks } from './types'
import type { ScheduleItem } from './useTodayBoardData'

interface EnhancedListViewProps extends MovementCallbacks {
  arriving: ScheduleItem[]
  departing: ScheduleItem[]
}

function ColumnHeader({
  icon,
  title,
  count,
}: {
  icon: React.ReactNode
  title: string
  count: number
}) {
  return (
    <div className="flex items-center gap-2 pb-2 shrink-0">
      {icon}
      <h3 className="text-sm font-semibold uppercase tracking-wide">{title}</h3>
      <Badge variant="secondary" className="h-5 text-[10px]">
        {count}
      </Badge>
    </div>
  )
}

export function EnhancedListView({ arriving, departing, onRowClick, onCheckIn, onCheckOut, onRegister, onShowDetails }: EnhancedListViewProps) {
  const [detailLevel, setDetailLevel] = useState<DetailLevel>('L1')
  const callbacks: MovementCallbacks = { onRowClick, onCheckIn, onCheckOut, onRegister, onShowDetails }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Control bar */}
      <div className="flex items-center justify-end pb-2 shrink-0">
        <DetailLevelToggle value={detailLevel} onChange={setDetailLevel} />
      </div>

      {/* Two-column grid */}
      <div className="grid grid-cols-2 gap-4 flex-1 min-h-0">
        {/* Arrivals */}
        <div className="flex flex-col min-h-0">
          <ColumnHeader
            icon={<PlaneLanding className="h-4 w-4 text-blue-600" />}
            title="Arrivals"
            count={arriving.length}
          />
          <div className="flex-1 min-h-0">
            <VirtualHourList items={arriving} detailLevel={detailLevel} callbacks={callbacks} />
          </div>
        </div>

        {/* Departures */}
        <div className="flex flex-col min-h-0">
          <ColumnHeader
            icon={<PlaneTakeoff className="h-4 w-4 text-orange-600" />}
            title="Departures"
            count={departing.length}
          />
          <div className="flex-1 min-h-0">
            <VirtualHourList items={departing} detailLevel={detailLevel} callbacks={callbacks} />
          </div>
        </div>
      </div>
    </div>
  )
}
